import {
  Box,
  Button,
  Grid,
  HStack,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Text,
  useMediaQuery,
  VStack,
} from "@chakra-ui/react";
import { Input } from "../../ui_components/reusables";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
import React, { useEffect, useMemo, useRef, useState } from "react";
import useAppStore from "../../store/appStore";
import { shallow } from "zustand/shallow";
import { useFirebase } from "../../wrappers/firebase";
import useUserStore from "../../store/userStore";
import { onSnapshot } from "firebase/firestore";
import MenuSidebar from "../../ui_components/menubar";
import { StripeWrapper, useStripe } from "../../wrappers/stripe";
import MusicPlayer from "../../ui_components/music/player";
import { FiPlus } from "react-icons/fi";
import MusicCard from "../../ui_components/music/musicCard";
import usePosts from "../../hooks/data/usePosts";
import NotificationsUI from "../../ui_components/notificaitons";
import MessagesUI from "../../ui_components/messages";
import CustomPortal from "../../ui_components/customPortal";
import useSearch from "../../hooks/data/useSearch";
import GrooveRoomsList from "./grooveRoom/roomList";
import PlaylistList from "./playlist/playlistList";
import User from "../../ui_components/user";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HlsAudioWrapper } from "../../wrappers/hlsAudio";
import ReportManager from "../../wrappers/report";
import { GrooveRoomProvider } from "../../wrappers/grooveRoom";
import { BiMusic } from "react-icons/bi";
import { TbMusic, TbShirt } from "react-icons/tb";
import { IoCubeSharp } from "react-icons/io5";
import { BsSearch } from "react-icons/bs";
import AnimatedLineLoader from "../../ui_components/animatedLoader";
import Post from "../../ui_components/post";
import useSidebarStore from "../../store/sidebarStore";
import Product from "../../ui_components/product";
import { AnimatePresence, motion } from "framer-motion";

function _AppLayout() {
  const [isMobile] = useMediaQuery("(max-width: 600px)");
  const lastRoute = useAppStore((state) => state.lastRoute, shallow);
  const setLastRoute = useAppStore((state) => state.setLastRoute);
  const navigate = useNavigate();
  const {
    id: stateUserId,
    customerId,
    username,
  } = useUserStore((state) => state.user, shallow);
  const setUser = useUserStore((state) => state.setUser);
  const { getData, getDocRef } = useFirebase();
  const searchRef = useRef();
  const {
    performSearch,
    searchResults,
    isPending: isSearchPending,
  } = useSearch();
  const { pathname } = useLocation();
  const { ConnectAccountStatus } = useStripe();
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const sidebarData = useSidebarStore((state) => state.sidebar, shallow);
  const renderRightSidebar = !!sidebarData?.type;

  useEffect(() => {
    if (lastRoute) {
      navigate(lastRoute);
      setLastRoute(null);
    }
  }, []);

  return (
    <Grid
      h="full"
      w="full"
      gridTemplateColumns={isMobile ? "1fr" : `230px minMax(0, 680px) 290px`}
      gridTemplateRows={isMobile ? "35px auto 1fr" : "75px 1fr"}
      gap={isMobile && "3"}
      transition={"all 300ms"}
      px={isMobile ? "2" : "5"}
      pr={!isMobile && "2"}
      py={isMobile ? "2" : "0"}
      justifyContent="space-between"
    >
      <MenuSidebar gridArea={isMobile ? "1 / 1 / 2 / 2" : "1 / 1 / 3 / 2"}>
        {/* <GrooveRoomsList /> */}
        <PlaylistList />
      </MenuSidebar>
      <Box
        gridArea={isMobile ? "2 / 1 / 3 / 2" : "1 / 2 / 2 / 3"}
        // px={!isMobile && "5"}
        pt={isMobile && "4"}
      >
        <Grid
          w="full"
          h="full"
          gridTemplateColumns={"1fr auto"}
          spacing={"2"}
          alignItems={"center"}
          columnGap={"2"}
          onFocus={() => setIsSearchFocused(true)}
        >
          <Grid
            w="full"
            role="group"
            ref={searchRef}
            gridTemplateRows={"auto auto"}
          >
            <HStack w="full" px={{ lg: "0", base: "4" }}>
              <Box
                color="gray.300"
                transition={"all 150ms ease"}
                fontSize={"16px"}
                _groupHover={{
                  color: "white",
                }}
                transformOrigin={"center"}
                _groupFocusWithin={{
                  transform: "rotate(90deg)",
                  fontSize: "19px",
                }}
              >
                <BsSearch />
              </Box>
              <Input
                placeholder="Search artists, tracks, effects & more..."
                size={"2xl"}
                fontSize={{ lg: "xl", md: "sm" }}
                fontWeight="semibold"
                w="full"
                h={isMobile ? "max-content" : "35px"}
                py={isMobile && "2"}
                onChange={(e) => performSearch({ query: e.target.value })}
                bg="transparent"
                border="0px"
                borderRadius="none"
                borderBottom="1px transparent"
                _placeholder={{
                  color: "gray.500",
                  transition: "color 150ms ease",
                }}
                _hover={{
                  _placeholder: {
                    color: "gray.300",
                  },
                }}
                _focus={{
                  boxShadow: "none",
                  borderColor: "transparent",
                  outline: "none",
                  _placeholder: {
                    color: "gray.300",
                  },
                }}
              />
            </HStack>
            <AnimatedLineLoader isLoading={isSearchPending} />
          </Grid>

          <CustomPortal
            targetRef={searchRef}
            isOpen={searchResults?.length > 0 && isSearchFocused}
            offset={6}
            containerProps={{
              onBlur: () => setIsSearchFocused(false),
            }}
          >
            <VStack
              w="full"
              maxH={"300px"}
              overflowY={"scroll"}
              background="#212123"
              border="1px solid #3e3e46"
              p="1"
            >
              <VStack
                w="full"
                h="max-content"
                align={"flex-start"}
                spacing={"0"}
              >
                {searchResults?.map(({ type, hits }) => {
                  return (
                    hits?.length > 0 && (
                      <React.Fragment key={`search_type_${type}`}>
                        <Text
                          color={"white"}
                          key={`label_${type}`}
                          textTransform={"capitalize"}
                          fontSize={"sm"}
                          px="2"
                          py="1"
                        >
                          {type === "posts" ? "Sounds" : type}
                        </Text>
                        {hits?.map((document) =>
                          type === "users" ? (
                            <User.SearchItem
                              key={`search_res_user_${document.objectID}`}
                              containerProps={{
                                as: Link,
                                to: `user/${document.username}`,
                                state: { queryId: document.queryId },
                              }}
                              {...document}
                            />
                          ) : (
                            <Post.SearchItem
                              key={`search_res_post_${document.objectID}`}
                              containerProps={{
                                as: Link,
                                to: `user/${document.username}/post/${document.objectID}`,
                                state: { queryId: document.queryId },
                              }}
                              {...document}
                            />
                          )
                        )}
                      </React.Fragment>
                    )
                  );
                })}
              </VStack>
            </VStack>
          </CustomPortal>
          {/* <PostCreator /> */}
        </Grid>
      </Box>
      <VStack
        gridArea={
          isMobile
            ? "3 / 1 / 4 / 2"
            : renderRightSidebar
            ? "2 / 2 / 3 / 3"
            : "2 / 2 / 3 / 4"
        }
        align={"center"}
        w="full"
        h="full"
        minW="0"
        maxW="full"
        overflow={"hidden"}
      >
        <Outlet />
      </VStack>
      {!isMobile && (
        <>
          <Grid
            id="test-item"
            gridArea={"1 / 3 / 2 / 4"}
            // borderLeft={"1px solid #2a2a2c"}
            pl="2"
            // pb="2"
            pos="relative"
            gridTemplateRows={"75px 1fr"}
            // justify={"flex-end"}
          >
            <HStack
              gridArea={"1 / 1 / 2 / 2"}
              align={"center"}
              justify={"flex-end"}
              pr="3"
            >
              <HStack spacing={"0"}>
                <MessagesUI />
                <NotificationsUI />
              </HStack>
              <ConnectAccountStatus />
              <User.NavigationButton />
            </HStack>
          </Grid>
          {renderRightSidebar && (
            <VStack
              transition={"opacity 300ms"}
              gridArea={"2 / 3 / 3 / 4"}
              overflowY={"scroll"}
              opacity={sidebarData.contents?.length > 0 ? 1 : 0}
            >
              <VStack
                w="full"
                align={"flex-start"}
                h="max-content"
                pb="5"
                spacing={"3"}
              >
                <Text fontSize={"sm"} fontWeight={"semibold"}>
                  {sidebarData.title}
                </Text>
                {sidebarData.contents?.map((objectId) =>
                  sidebarData.type === "post" ? (
                    <Post.ListItem
                      key={`sidebar_content_${objectId}`}
                      id={objectId}
                      variant="small"
                    />
                  ) : (
                    <Product.ListItem
                      key={`sidebar_content_${objectId}`}
                      id={objectId}
                      w="full"
                      imageContainerProps={{
                        h: "70px",
                        aspectRatio: "1 / 1",
                      }}
                    />
                  )
                )}
              </VStack>
            </VStack>
          )}
        </>
      )}
      <Portal>
        <MusicPlayer
          pos="absolute"
          bottom={"2"}
          left="2"
          right={"0"}
          zIndex={"99"}
        />
      </Portal>
    </Grid>
  );
}

const AppLayout = ({ children }) => {
  const { id: stateUserId } = useUserStore((state) => state.user, shallow);
  const { currentUser } = useFirebase();
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            staleTime: Infinity,
            gcTime: Infinity,
          },
        },
      }),
    []
  );

  const shouldShowLoader = !stateUserId || !currentUser || !queryClient;

  return (
    <AnimatePresence mode="wait">
      {shouldShowLoader ? (
        <motion.div
          key={"loader"}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            height: "100%",
            width: "100%",
            display: "flex",
            alignItems: "center",
          }}
        >
          <AnimatedLineLoader isLoading={true} />
        </motion.div>
      ) : (
        <motion.div
          style={{ height: "100%", width: "100%" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <QueryClientProvider client={queryClient}>
            <StripeWrapper>
              <HlsAudioWrapper>
                <ReportManager>
                  <_AppLayout />
                </ReportManager>
              </HlsAudioWrapper>
            </StripeWrapper>
          </QueryClientProvider>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AppLayout;
